import {
  CustomQuestionnaireAnswer,
  CustomQuestionnaireAnswerRead,
  CustomQuestionnaireQuestion,
  CustomQuestionnaireQuestionRuleCondition,
  CustomQuestionnaireStep,
} from '@api';
import { CustomQuestionnaireAnswerSetExtended } from '@stores';
import { FieldValues, UseFormGetValues } from 'react-hook-form';
import * as yup from 'yup';

type QuestionnaireAnswer = Pick<CustomQuestionnaireAnswer, 'freeTextAnswer' | 'multiSelectAnswer' | 'radioAnswer'>;

const mapValueToAnswerType = (value: string | number | string[], questionType: string): QuestionnaireAnswer => {
  let answer: QuestionnaireAnswer = {};
  if (value === null || value === undefined) {
    return null;
  } // it can happen for hidden questions => remove answer from payload
  if (questionType === 'free_text') {
    answer = { freeTextAnswer: { id: null, value: value as string } };
  }
  if (questionType === 'radio') {
    let parsedValue;
    if (value) parsedValue = parseInt(value as string, 10);
    answer = { radioAnswer: { value: parsedValue } };
  }
  if (questionType === 'dropdown') {
    answer = { radioAnswer: { value: parseInt(value as string, 10) } };
  }
  if (questionType === 'multi_select') {
    answer = {
      multiSelectAnswer: {
        value: (Array.isArray(value) ? value : [value]).map(v => parseInt(v as string, 10)) as number[], // fix for issue when question type is changed from radio/dropdown to multi_select
      },
    };
  }
  return answer;
};

const mapValueToAnswerOption = (answer: CustomQuestionnaireAnswerRead, newQuestionnaireAnswer: boolean = false) => {
  if (answer?.freeTextAnswer) {
    const answerObject = { ...answer, freeTextAnswer: { value: answer.freeTextAnswer.value } };
    // when creating new questionnaire answer, we don't want to send the id
    if (answer.id === null || newQuestionnaireAnswer) {
      delete answerObject.id;
    }
    return answerObject;
  }
  if (answer?.radioAnswer) {
    const answerObject = { ...answer, radioAnswer: { value: answer.radioAnswer.value } };
    if (answer.id === null || newQuestionnaireAnswer) {
      delete answerObject.id;
    }
    return answerObject;
  }
  if (answer?.multiSelectAnswer) {
    const answerObject = { ...answer, multiSelectAnswer: { value: answer.multiSelectAnswer.value.map(v => v) } };
    if (answer.id === null || newQuestionnaireAnswer) {
      delete answerObject.id;
    }
    return answerObject;
  }
  return null;
};

const getAnswerValue = (answer: QuestionnaireAnswer | CustomQuestionnaireAnswerRead) => {
  if (answer?.freeTextAnswer) {
    return answer.freeTextAnswer.value;
  }
  if (answer?.radioAnswer) {
    return answer.radioAnswer.value;
  }
  if (answer?.multiSelectAnswer) {
    return answer.multiSelectAnswer.value.map(v => v.toString());
  }
  return null;
};

const getPopulatedAnswerValue = (answer: CustomQuestionnaireAnswerRead) => {
  if (answer?.freeTextAnswer) {
    return answer.freeTextAnswer.value;
  }
  if (answer?.radioAnswer) {
    return answer.radioAnswer.value.value;
  }
  if (answer?.multiSelectAnswer) {
    return answer.multiSelectAnswer.value.map(v => v.value).join(', ');
  }
  return null;
};

const getConditionalQuestionValue = (
  getValues: UseFormGetValues<FieldValues>,
  step: CustomQuestionnaireStep,
  condition: CustomQuestionnaireQuestionRuleCondition,
  data: CustomQuestionnaireAnswerSetExtended,
) => {
  let conditionalQuestionValue;
  if (step.questions.some(q => q.id === condition.question)) {
    conditionalQuestionValue = getValues(condition.question.toString()); // get value from form
  } else {
    conditionalQuestionValue = getAnswerValue(data.answers?.find(answer => answer.questionId === condition.question)); // get value from questionnaireData
  }

  return conditionalQuestionValue;
};

const isConditionMatched = (
  condition: CustomQuestionnaireQuestionRuleCondition,
  conditionalQuestionValue: number,
  conditionValue: number,
) => {
  if (condition.operator === 'equals' && conditionalQuestionValue === conditionValue) {
    return true;
  }
  if (condition.operator === 'not_equals' && conditionalQuestionValue !== conditionValue) {
    return true;
  }
  return false;
};

const createYupShape = (schema, question: CustomQuestionnaireQuestion) => {
  const shape = schema;
  if (question.questionType === 'free_text') {
    // free text can be required or not required
    if (question.freeTextRequired) {
      shape[question.id.toString()] = yup.string().required('This field is required.');
    } else {
      shape[question.id.toString()] = yup.string();
    }
  }
  // other question types are always required
  if (question.questionType === 'radio' || question.questionType === 'dropdown') {
    shape[question.id.toString()] = yup.string().required('Please select one option.');
  }
  if (question.questionType === 'multi_select') {
    shape[question.id.toString()] = yup
      .array()
      .of(yup.string())
      .min(1, 'Please select at least one option.')
      .required('Please select at least one option.');
  }
  return shape;
};

export const CustomHelper = {
  mapValueToAnswerType,
  getAnswerValue,
  createYupShape,
  getConditionalQuestionValue,
  isConditionMatched,
  getPopulatedAnswerValue,
  mapValueToAnswerOption,
};
